import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from './Button';
import { Pill } from './Pill';
import { TabNav } from './TabNav';
import { Card } from './Card';
import { EmptyState } from './EmptyState';

/**
 * V2PageShell — shared page chrome for Phases 7-11 v2 surfaces.
 *
 * The Phase 1-6 pages each got a hand-crafted layout because they're
 * the high-leverage redesigns. For Phases 7-11 we bring the v2 chrome
 * (header + actions + optional tabs + breadcrumb back-link) consistently
 * with one helper, and let each page concentrate on its actual content.
 *
 * Two modes:
 *   1. Children mode — pass arbitrary content as children
 *   2. Tabs mode    — pass `tabs` + `activeTabId` + `onTabChange`;
 *                     children represents the active tab body
 *
 * @param {Object} props
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.subtitle]
 * @param {React.ReactNode} [props.actions]
 * @param {React.ReactNode} [props.tags]            — pill row under title
 * @param {string}          [props.backTo]          — show "Back" link
 * @param {string}          [props.backLabel='Back']
 * @param {Array}           [props.tabs]            — TabNav items
 * @param {string}          [props.activeTabId]
 * @param {(id: string) => void} [props.onTabChange]
 * @param {React.ReactNode} props.children
 * @param {string}          [props.maxWidth='1400px']
 */
export function V2PageShell({
  title, subtitle, actions, tags,
  backTo, backLabel = 'Back',
  tabs, activeTabId, onTabChange,
  children,
  maxWidth = '1400px',
}) {
  const navigate = useNavigate();
  return (
    <div className="min-h-full bg-v2-page text-v2-ink">
      <header className="bg-v2-surface border-b border-v2-border-subtle px-7 py-5">
        <div className="mx-auto" style={{ maxWidth }}>
          {backTo && (
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<ArrowLeft />}
              onClick={() => navigate(backTo)}
              className="mb-3"
            >
              {backLabel}
            </Button>
          )}
          <div className="flex items-start gap-4">
            <div className="min-w-0 flex-1">
              {tags && <div className="flex items-center gap-2 mb-1.5">{tags}</div>}
              <h1 className="text-[22px] font-bold tracking-tight m-0 text-v2-ink">{title}</h1>
              {subtitle && (
                <p className="text-[13px] text-v2-ink-secondary mt-0.5">{subtitle}</p>
              )}
            </div>
            {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
          </div>
        </div>
      </header>
      {tabs && tabs.length > 0 && (
        <div className="bg-v2-surface px-7">
          <div className="mx-auto" style={{ maxWidth }}>
            <TabNav
              tabs={tabs}
              activeId={activeTabId}
              onChange={onTabChange}
            />
          </div>
        </div>
      )}
      <div className="px-7 py-6">
        <div className="mx-auto" style={{ maxWidth }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * V2LinkOutCard — standardised link-out card. Used by section wrappers
 * that route to a deeper detail view rather than rendering it inline.
 */
export function V2LinkOutCard({ icon, title, description, linkTo, linkLabel = 'Open detail view' }) {
  const navigate = useNavigate();
  return (
    <Card>
      <Card.Body>
        <EmptyState
          icon={icon}
          title={title}
          description={description}
          primaryAction={
            <Button variant="primary" iconLeft={<ExternalLink />} onClick={() => navigate(linkTo)}>
              {linkLabel}
            </Button>
          }
        />
      </Card.Body>
    </Card>
  );
}

export { Pill }; // re-export for convenience to wrapper pages
export default V2PageShell;
